
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createDeviceWebSocket, getWsToken } from '../api/websocket'
import { useAlertToast, type AlertData } from './alertToastStore'
import { useAuth } from './authStore'

export type BeltConnectionState =
  | 'never_connected'
  | 'live'
  | 'signal_weak'
  | 'reconnecting'
  | 'offline'

export interface WebSocketData {
  isConnected: boolean
  isBeltOnline: boolean
  lastMessageTimestamp: string | null
  lastMessageAgeSeconds: number | null
  bpm: number | null
  fetalBpm: number | null
  maternalBpm: number | null
  piezoValue: number
  sensor2Value: number
  waveformData: number[]
  babyWaveBatch: number[]
  motherWaveBatch: number[]
  hasWaveformSignal: boolean
  cameraFrame: string | null
  cameraStatus: { isLive: boolean; lastOnlineAt: string | null } | null
  activeSessionId: string | null
  activeSessionUid: string | null
  activeSessionSource: SessionEventSource | null
  hasSessionState: boolean
}

export type SessionEventSource = 'app' | 'wear'

interface DeviceMessage extends Record<string, unknown> {
  type?: string
  alert?: AlertData
  frame?: string
  isLive?: boolean
  lastOnlineAt?: string | null
  ts?: string
  sessionId?: string | number | null
  sessionUid?: string | null
  source?: string | null
}

const FETAL_WAVEFORM_KEYS = ['baby_wave']
const LEGACY_WAVEFORM_KEYS = [
  'piezo',
  'sensor1',
  'waveform',
  'waveformData',
  'piezoSamples',
  'piezo_samples',
  'samples',
]

const initialWebSocketData: WebSocketData = {
  isConnected: false,
  isBeltOnline: false,
  lastMessageTimestamp: null,
  lastMessageAgeSeconds: null,
  bpm: null,
  fetalBpm: null,
  maternalBpm: null,
  piezoValue: 0,
  sensor2Value: 0,
  waveformData: [],
  babyWaveBatch: [],
  motherWaveBatch: [],
  hasWaveformSignal: false,
  cameraFrame: null,
  cameraStatus: null,
  activeSessionId: null,
  activeSessionUid: null,
  activeSessionSource: null,
  hasSessionState: false,
}

const DeviceWebSocketContext = createContext<WebSocketData | undefined>(undefined)

export function getBeltConnectionState(
  lastMessageAgeSeconds: number | null,
  isWaveformPaused = false,
): BeltConnectionState {
  if (lastMessageAgeSeconds === null) return 'never_connected'
  if (lastMessageAgeSeconds <= 5) return 'live'
  if (lastMessageAgeSeconds <= 15) {
    return isWaveformPaused ? 'signal_weak' : 'live'
  }
  if (lastMessageAgeSeconds <= 20) return 'reconnecting'
  return 'offline'
}

function getMessageAgeSeconds(timestamp: string): number | null {
  const recordedAt = Date.parse(timestamp)
  if (Number.isNaN(recordedAt)) return null
  return Math.max(0, Math.floor((Date.now() - recordedAt) / 1000))
}

function getMessageTimestamp(message: Record<string, unknown>): string | null {
  if (typeof message.ts !== 'string') return null
  return getMessageAgeSeconds(message.ts) === null ? null : message.ts
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || !value.trim()) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function getNumericValue(
  payload: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const key of keys) {
    const numericValue = toFiniteNumber(payload[key])
    if (numericValue !== null) return numericValue
  }
  return null
}

function getSampleValue(sample: unknown): number | null {
  const scalarValue = toFiniteNumber(sample)
  if (scalarValue !== null) return scalarValue
  if (typeof sample !== 'object' || sample === null || Array.isArray(sample)) {
    return null
  }
  return toFiniteNumber((sample as Record<string, unknown>).v)
}

function toNumericSamples(value: unknown): number[] {
  if (!Array.isArray(value)) {
    const numericValue = getSampleValue(value)
    return numericValue === null ? [] : [numericValue]
  }
  return value.flatMap((sample) => {
    const numericValue = getSampleValue(sample)
    return numericValue === null ? [] : [numericValue]
  })
}

function getBabyWaveformSamples(payload: Record<string, unknown>): number[] {
  for (const key of FETAL_WAVEFORM_KEYS) {
    const samples = toNumericSamples(payload[key])
    if (samples.length > 0) return samples
  }
  for (const key of LEGACY_WAVEFORM_KEYS) {
    const samples = toNumericSamples(payload[key])
    if (samples.length > 0) return samples
  }
  return []
}

function getMotherWaveformSamples(payload: Record<string, unknown>): number[] {
  return toNumericSamples(payload.mother_wave)
}

function toSessionIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value !== 'string' || !value.trim()) return null
  return value
}

function toSessionSource(value: unknown): SessionEventSource | null {
  return value === 'app' || value === 'wear' ? value : null
}

export function DeviceWebSocketProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const { addAlertToast } = useAlertToast()
  const [data, setData] = useState<WebSocketData>(initialWebSocketData)
  const wsRef = useRef<WebSocket | null>(null)
  const waveformRef = useRef<number[]>([])
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<number | null>(null)
  const intentionalCloseRef = useRef(false)
  const intentionalCloseSocketsRef = useRef(new Set<WebSocket>())
  const connectionSuspendedRef = useRef(false)
  const isConnectingRef = useRef(false)
  const resumeRequestedRef = useRef(false)
  const addAlertToastRef = useRef(addAlertToast)

  useEffect(() => {
    addAlertToastRef.current = addAlertToast
  }, [addAlertToast])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    let isActive = true

    const updateData = (updater: (previous: WebSocketData) => WebSocketData) => {
      if (isActive) setData(updater)
    }

    const closeSocket = (intentional = false) => {
      clearReconnectTimer()
      if (intentional) {
        intentionalCloseRef.current = true
        connectionSuspendedRef.current = true
      }
      if (wsRef.current) {
        if (intentional) intentionalCloseSocketsRef.current.add(wsRef.current)
        wsRef.current.close()
        wsRef.current = null
      }
    }

    if (!token) {
      intentionalCloseRef.current = false
      intentionalCloseSocketsRef.current.clear()
      connectionSuspendedRef.current = false
      isConnectingRef.current = false
      resumeRequestedRef.current = false
      closeSocket()
      waveformRef.current = []
      setData(initialWebSocketData)
      return () => {
        isActive = false
      }
    }

    const updateMessageAge = () => {
      updateData((previous) => {
        if (!previous.lastMessageTimestamp) return previous
        const ageSeconds = getMessageAgeSeconds(previous.lastMessageTimestamp)
        if (ageSeconds === null || ageSeconds === previous.lastMessageAgeSeconds) {
          return previous
        }
        return { ...previous, lastMessageAgeSeconds: ageSeconds }
      })
    }

    const freshnessInterval = window.setInterval(updateMessageAge, 1000)

    let connect: () => Promise<void>
    const scheduleReconnect = () => {
      if (
        !isActive ||
        connectionSuspendedRef.current ||
        reconnectAttemptsRef.current >= 5
      ) {
        return
      }
      reconnectAttemptsRef.current += 1
      reconnectTimerRef.current = window.setTimeout(() => {
        void connect()
      }, 5000)
    }

    connect = async () => {
      if (
        !isActive ||
        connectionSuspendedRef.current ||
        isConnectingRef.current ||
        wsRef.current
      ) {
        return
      }
      isConnectingRef.current = true
      try {
        const { token: wsToken } = await getWsToken(token)
        if (!isActive || connectionSuspendedRef.current) return

        const ws = createDeviceWebSocket(wsToken)
        wsRef.current = ws

        ws.onopen = () => {
          if (!isActive) return
          reconnectAttemptsRef.current = 0
          intentionalCloseRef.current = false
          connectionSuspendedRef.current = false
          updateData((previous) => ({ ...previous, isConnected: true }))
        }

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as DeviceMessage
            const messageTimestamp = getMessageTimestamp(message)

            if (message.type === 'alert' && message.alert) {
              addAlertToastRef.current(message.alert)
            }

            const payload = message
            const fetalReading = toFiniteNumber(payload.baby_bpm)
            const maternalReading = toFiniteNumber(payload.mother_bpm)
            const currentBpm = getNumericValue(payload, ['bpm', 'baby_bpm'])
            const currentSensor2 = getNumericValue(payload, ['sensor2', 'sensor_2'])
            const babySamples = getBabyWaveformSamples(payload)
            const motherSamples = getMotherWaveformSamples(payload)

            updateData((previous) => {
              let nextData = messageTimestamp
                ? {
                    ...previous,
                    lastMessageTimestamp: messageTimestamp,
                    lastMessageAgeSeconds: getMessageAgeSeconds(messageTimestamp),
                  }
                : previous

              if (message.type === 'device_online') {
                nextData = { ...nextData, isBeltOnline: true }
              }
              if (message.type === 'device_offline') {
                nextData = { ...nextData, isBeltOnline: false }
              }
              // Sessions can start/stop from the app or from belt wear
              // detection; both are handled identically.
              if (message.type === 'session.started') {
                nextData = {
                  ...nextData,
                  activeSessionId: toSessionIdentifier(message.sessionId),
                  activeSessionUid: toSessionIdentifier(message.sessionUid),
                  activeSessionSource: toSessionSource(message.source),
                  hasSessionState: true,
                }
              }
              if (message.type === 'session.stopped') {
                nextData = {
                  ...nextData,
                  activeSessionId: null,
                  activeSessionUid: null,
                  activeSessionSource: toSessionSource(message.source),
                  hasSessionState: true,
                }
              }
              if (fetalReading !== null) {
                nextData = { ...nextData, fetalBpm: fetalReading, bpm: fetalReading }
              }
              if (maternalReading !== null) {
                nextData = { ...nextData, maternalBpm: maternalReading }
              }
              if (currentBpm !== null) {
                nextData = { ...nextData, bpm: currentBpm }
              }
              if (currentSensor2 !== null) {
                nextData = { ...nextData, sensor2Value: currentSensor2 }
              }
              if (babySamples.length > 0) {
                waveformRef.current = [...waveformRef.current, ...babySamples].slice(-400)
                nextData = {
                  ...nextData,
                  waveformData: waveformRef.current,
                  babyWaveBatch: babySamples,
                  piezoValue: babySamples[babySamples.length - 1],
                  hasWaveformSignal: waveformRef.current.length > 1,
                }
              }
              if (motherSamples.length > 0) {
                nextData = { ...nextData, motherWaveBatch: motherSamples }
              }
              if (message.type === 'camera.frame' && message.frame) {
                nextData = {
                  ...nextData,
                  cameraFrame: message.frame,
                  cameraStatus: nextData.cameraStatus?.isLive
                    ? nextData.cameraStatus
                    : {
                        isLive: true,
                        lastOnlineAt: nextData.cameraStatus?.lastOnlineAt ?? null,
                      },
                }
              }
              if (message.type === 'camera.status') {
                nextData = {
                  ...nextData,
                  cameraStatus: {
                    isLive: Boolean(message.isLive),
                    lastOnlineAt: message.lastOnlineAt ?? null,
                  },
                  cameraFrame: message.isLive ? nextData.cameraFrame : null,
                }
              }
              return nextData
            })
          } catch {
            // Ignore malformed device messages and continue streaming.
          }
        }

        ws.onclose = () => {
          if (!isActive) return
          const wasMarkedIntentional = intentionalCloseSocketsRef.current.delete(ws)
          const wasIntentional =
            intentionalCloseRef.current && wasMarkedIntentional
          const wasCurrentSocket = wsRef.current === ws
          if (wasCurrentSocket) {
            wsRef.current = null
            updateData((previous) => ({ ...previous, isConnected: false }))
          } else if (!wsRef.current) {
            updateData((previous) => ({ ...previous, isConnected: false }))
          }
          if (!wasIntentional && wasCurrentSocket) scheduleReconnect()
        }

        ws.onerror = () => {
          if (isActive) {
            updateData((previous) => ({ ...previous, isConnected: false }))
          }
        }
      } catch {
        if (!connectionSuspendedRef.current) scheduleReconnect()
      } finally {
        isConnectingRef.current = false
        if (
          resumeRequestedRef.current &&
          isActive &&
          !connectionSuspendedRef.current &&
          !wsRef.current
        ) {
          resumeRequestedRef.current = false
          void connect()
        }
      }
    }

    let visibilityTimer: number | null = null
    let inactivityTimer: number | null = null
    const interactionEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ]
    const clearVisibilityTimer = () => {
      if (visibilityTimer !== null) {
        window.clearTimeout(visibilityTimer)
        visibilityTimer = null
      }
    }
    const clearInactivityTimer = () => {
      if (inactivityTimer !== null) {
        window.clearTimeout(inactivityTimer)
        inactivityTimer = null
      }
    }
    const resumeConnection = () => {
      if (!isActive || wsRef.current) return
      clearReconnectTimer()
      reconnectAttemptsRef.current = 0
      connectionSuspendedRef.current = false
      if (isConnectingRef.current) {
        resumeRequestedRef.current = true
        return
      }
      void connect()
    }
    const scheduleInactivityClose = () => {
      clearInactivityTimer()
      if (document.visibilityState !== 'visible') return
      inactivityTimer = window.setTimeout(() => {
        if (document.visibilityState === 'visible') closeSocket(true)
      }, 5 * 60 * 1000)
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearInactivityTimer()
        clearVisibilityTimer()
        visibilityTimer = window.setTimeout(() => {
          if (document.visibilityState === 'hidden') closeSocket(true)
        }, 60 * 1000)
        return
      }
      clearVisibilityTimer()
      resumeConnection()
      scheduleInactivityClose()
    }
    const handleInteraction = () => {
      if (document.visibilityState !== 'visible') return
      resumeConnection()
      scheduleInactivityClose()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, handleInteraction),
    )
    if (document.visibilityState === 'hidden') {
      handleVisibilityChange()
    } else {
      scheduleInactivityClose()
    }
    void connect()

    return () => {
      isActive = false
      clearVisibilityTimer()
      clearInactivityTimer()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      )
      window.clearInterval(freshnessInterval)
      closeSocket()
    }
  }, [clearReconnectTimer, token])

  return React.createElement(DeviceWebSocketContext.Provider, { value: data }, children)
}

export function useDeviceWebSocketStore(): WebSocketData {
  const context = useContext(DeviceWebSocketContext)
  if (context === undefined) {
    throw new Error(
      'useDeviceWebSocketStore must be used within a DeviceWebSocketProvider',
    )
  }
  return context
}

